
import React, { useEffect, useState, useRef } from 'react';
import { Card, Button, Badge } from '../components/UI';
import { apiRequest } from '../utils/api';
import { useAuth } from '../components/AuthContext';
import { UserRole } from '../types';
import { Navigate } from 'react-router-dom';
import { MessageCircle, Send, Shield, User } from 'lucide-react';
import { API_BASE_URL } from '../config';
import { PageHero } from '../components/PageHero';

interface ChatMessage {
    id: number;
    user_id: string;
    username: string;
    avatar_url?: string;
    message: string;
    created_at: string;
    roles?: string; // Comma separated
}

export const StewardChat: React.FC = () => {
    const { user } = useAuth();
    const [messages, setMessages] = useState<ChatMessage[]>([]);
    const [input, setInput] = useState('');
    const [loading, setLoading] = useState(false);
    const textareaRef = useRef<HTMLTextAreaElement>(null);
    const bottomRef = useRef<HTMLDivElement>(null);
    const intervalRef = useRef<number | null>(null);

    const isStaff = user?.roles.includes(UserRole.ADMIN) || user?.roles.includes(UserRole.WEBADMIN) || user?.roles.includes(UserRole.STEWARD);

    if (!user || !isStaff) {
        return <Navigate to="/" />;
    }

    const loadMessages = async () => {
        try {
            const res = await apiRequest<ChatMessage[]>(`steward_chat.php?userId=${user.id}`);
            // Check if response is valid array, ignore errors silently to not spam console
            if (Array.isArray(res)) {
                setMessages(res);
            }
        } catch (e) { }
    };

    useEffect(() => {
        loadMessages();
        intervalRef.current = window.setInterval(loadMessages, 3000); // Poll every 3s
        return () => { if(intervalRef.current) clearInterval(intervalRef.current); };
    }, []);

    // Auto-scroll only on new message count change
    useEffect(() => {
        if (bottomRef.current) {
            bottomRef.current.scrollIntoView({ behavior: 'smooth' });
        }
    }, [messages.length]);

    const handleSend = async () => {
        if (!input.trim()) return;
        setLoading(true);
        try {
            await apiRequest('steward_chat.php', 'POST', { userId: user.id, message: input });
            setInput('');
            loadMessages();
        } catch (e: any) { alert(e.message); }
        finally { setLoading(false); }
    };

    const getUrl = (url?: string) => {
        if (!url) return undefined;
        if (url.startsWith('http') || url.startsWith('data:')) return url;
        const baseUrl = API_BASE_URL === '.' ? '' : API_BASE_URL;
        return baseUrl ? `${baseUrl}/${url}` : url;
    };

    const getRoleBadge = (rolesStr?: string) => {
        if (!rolesStr) return null;
        const roles = rolesStr.split(',');
        if (roles.includes('WEBADMIN')) return <span className="text-[9px] bg-blue-900 text-blue-300 px-1 rounded border border-blue-700">DEV</span>;
        if (roles.includes('ADMIN')) return <span className="text-[9px] bg-red-900 text-red-300 px-1 rounded border border-red-700">ADMIN</span>;
        if (roles.includes('STEWARD')) return <span className="text-[9px] bg-yellow-900 text-yellow-300 px-1 rounded border border-yellow-700">STEWARD</span>;
        return null;
    };

    return (
        <div className="mx-auto flex min-h-[calc(100vh-120px)] w-full max-w-7xl flex-col gap-5 animate-fade-in pb-4">
            <PageHero
                eyebrow="Interner Kanal"
                title="Steward-Chat"
                description="Der geschützte Kommunikationsraum für Rennleitung und Stewards – konzentriert, nachvollziehbar und nah am Renngeschehen."
                icon={Shield}
                accent="#eab308"
                stats={[{ label: 'Nachrichten', value: messages.length }, { label: 'Status', value: 'Intern' }]}
            />

            <div className="relative flex min-h-[520px] flex-1 flex-col overflow-hidden rounded-[2rem] border border-white/[0.085] bg-gradient-to-br from-[#171a24] to-[#0e1017] shadow-[0_28px_80px_rgba(0,0,0,0.3)]">
                {/* Background Pattern */}
                <div className="absolute inset-0 opacity-5 pointer-events-none" style={{
                    backgroundImage: 'radial-gradient(#ffffff 1px, transparent 1px)',
                    backgroundSize: '20px 20px'
                }}></div>

                {/* Messages Area */}
                <div className="flex-1 overflow-y-auto p-4 space-y-4 relative z-10 custom-scrollbar">
                    {messages.length === 0 && (
                        <div className="flex flex-col items-center justify-center h-full text-gray-600 space-y-2">
                            <MessageCircle size={48} className="opacity-20"/>
                            <p className="text-sm italic">Keine Nachrichten vorhanden.</p>
                            <p className="text-xs">Schreibe die erste Nachricht an das Team.</p>
                        </div>
                    )}
                    
                    {messages.map((msg, idx) => {
                        const isMe = msg.user_id === user.id;
                        const showHeader = idx === 0 || messages[idx-1].user_id !== msg.user_id || (new Date(msg.created_at).getTime() - new Date(messages[idx-1].created_at).getTime() > 300000);

                        return (
                            <div key={idx} className={`flex flex-col ${isMe ? 'items-end' : 'items-start'}`}>
                                {showHeader && (
                                    <div className={`flex items-center gap-2 mb-1 px-1 ${isMe ? 'flex-row-reverse' : ''}`}>
                                        <div className="w-6 h-6 rounded-full bg-gray-800 border border-gray-600 overflow-hidden flex items-center justify-center">
                                            {msg.avatar_url ? <img src={getUrl(msg.avatar_url)} className="w-full h-full object-cover"/> : <User size={12} className="text-gray-400"/>}
                                        </div>
                                        <span className="text-xs font-bold text-gray-300">{msg.username}</span>
                                        {getRoleBadge(msg.roles)}
                                        <span className="text-[9px] text-gray-600">{new Date(msg.created_at).toLocaleTimeString([], {hour:'2-digit', minute:'2-digit'})}</span>
                                    </div>
                                )}
                                <div 
                                    className={`px-4 py-2 rounded-2xl max-w-[85%] text-sm shadow-sm break-words whitespace-pre-wrap ${
                                        isMe 
                                        ? 'bg-f1-red text-white rounded-tr-sm' 
                                        : 'bg-gray-800 text-gray-200 border border-gray-700 rounded-tl-sm'
                                    }`}
                                >
                                    {msg.message}
                                </div>
                            </div>
                        );
                    })}
                    <div ref={bottomRef} />
                </div>

                {/* Input Area */}
                <div className="relative z-20 shrink-0 border-t border-white/[0.07] bg-black/30 p-4">
                    <div className="flex gap-2 items-end">
                        <textarea 
                            ref={textareaRef}
                            className="flex-1 bg-gray-800 border border-gray-600 rounded-lg px-4 py-3 text-white focus:border-f1-red focus:bg-[#1a1a20] outline-none transition-all placeholder-gray-500 shadow-inner resize-none h-12 min-h-[48px] max-h-32 custom-scrollbar"
                            placeholder="Nachricht an das Team... (Enter für Zeilenumbruch, Button zum Senden)"
                            value={input}
                            onChange={e => {
                                setInput(e.target.value);
                                // Auto-resize
                                e.target.style.height = 'inherit';
                                e.target.style.height = `${Math.min(e.target.scrollHeight, 128)}px`;
                            }}
                            onKeyDown={e => {
                                if (e.key === 'Enter' && e.ctrlKey) {
                                    e.preventDefault();
                                    handleSend();
                                }
                            }}
                            autoFocus
                        />
                        <Button 
                            onClick={handleSend} 
                            disabled={!input.trim() || loading} 
                            icon={Send} 
                            className={`w-14 h-12 flex justify-center rounded-lg ${!input.trim() ? 'opacity-50' : 'hover:scale-105 active:scale-95'}`}
                        />
                    </div>
                </div>
            </div>
        </div>
    );
};
